import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconLink, IconPaperclip } from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import { createHostedPage, resolveExternalArticleLink, updateArticle } from '../services/api';

function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function randomSuffix() {
  return Math.random().toString(36).slice(2, 7);
}

function deriveDomain(url) {
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
}

export default function AviaryV2ArticleNewLinkPage() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const back = location.state?.back || { path: '/v2/articles/new', label: 'Add' };
  const defaultRole = location.state?.defaultRole || 'promo';

  const [url, setUrl] = useState('');
  const [resolved, setResolved] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);

  const canUse = Boolean(resolved?.ok && resolved?.reachable && url.trim());
  const resolvedTitle = String(resolved?.title || '').trim();
  const resolvedDomain = resolved?.domain || deriveDomain(url);
  const bannerDomain = resolved?.domain || 'the source site';

  const previewDescription = useMemo(() => {
    return String(resolved?.description || '').trim() || 'No description found on that page.';
  }, [resolved?.description]);

  async function handleFetch() {
    const trimmed = url.trim();
    if (!trimmed) return;
    setFetching(true);
    try {
      const data = await resolveExternalArticleLink(api, trimmed);
      setResolved(data);
      if (!data.ok || !data.reachable) {
        notifications.show({
          title: 'Could not resolve this link',
          message: data.error || 'That URL did not respond as expected.',
          color: 'red',
        });
      }
    } catch (err) {
      const message = err.response?.data?.error || err.message;
      setResolved({
        ok: false,
        reachable: false,
        title: '',
        description: '',
        domain: deriveDomain(trimmed),
        error: message,
      });
      notifications.show({
        title: 'Fetch failed',
        message,
        color: 'red',
      });
    } finally {
      setFetching(false);
    }
  }

  async function handleUseAndPromote() {
    if (!canUse) return;
    setSaving(true);
    try {
      const base = slugify(resolvedTitle || resolvedDomain || 'article-link');
      const slug = `${base || 'article-link'}-${randomSuffix()}`;
      const page = await createHostedPage(api, {
        kind: 'article',
        title: resolvedTitle || resolvedDomain || 'Linked article',
        slug,
        linkedUrl: url.trim(),
        articleStatus: 'draft',
        originNote: 'Linked article',
      });
      notifications.show({
        title: 'Article link added',
        message: `"${page.title || 'Linked article'}" is ready to promote.`,
        color: 'sage',
      });
      if (defaultRole === 'context') {
        await updateArticle(api, page.slug, { articleRole: 'context' });
      }
      const nextPath = defaultRole === 'context'
        ? '/v2/knowledge-base'
        : `/v2/articles/${page.slug}/promote`;
      navigate(nextPath, {
        state: { back: { path: '/v2/articles/new/link', label: 'Add a link' }, defaultRole },
      });
    } catch (err) {
      notifications.show({
        title: 'Could not create article',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box style={{ padding: '32px 24px 120px', maxWidth: 860, margin: '0 auto' }}>
      <Stack gap="lg">
        <Group gap={8} wrap="nowrap">
          <UnstyledButton
            onClick={() => navigate('/v2/articles')}
            style={{ color: 'var(--aviary-muted)', fontSize: 13 }}
          >
            Articles
          </UnstyledButton>
          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>/</Text>
          <UnstyledButton
            onClick={() => navigate('/v2/articles/new')}
            style={{ color: 'var(--aviary-muted)', fontSize: 13 }}
          >
            Add
          </UnstyledButton>
          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>/</Text>
          <Text size="xs" style={{ color: 'var(--aviary-ink)', fontWeight: 600 }}>
            Link
          </Text>
        </Group>

        <Stack gap={4}>
          <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28 }}>
            Add a link
          </Text>
          <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
            Paste the URL of an article you&apos;ve already published. The Aviary uses it as the destination - no copy is hosted here.
          </Text>
        </Stack>

        <Paper p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text size="xs" fw={700} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3 }}>
              DESTINATION URL
            </Text>
            <Group align="flex-end" wrap="nowrap">
              <TextInput
                value={url}
                onChange={(e) => setUrl(e.currentTarget.value)}
                placeholder="https://example.com/article"
                leftSection={<IconPaperclip size={15} />}
                style={{ flex: 1 }}
              />
              <Button color="sage" onClick={handleFetch} loading={fetching} disabled={!url.trim()}>
                Fetch
              </Button>
            </Group>
          </Stack>
        </Paper>

        {resolved && (
          <Stack gap="xs">
            <Text size="xs" fw={700} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3 }}>
              RESOLVED - confirm this is the right page
            </Text>
            <Paper p="md" radius="md" withBorder>
              <Group align="stretch" wrap="nowrap">
                <Box
                  style={{
                    width: 96,
                    minWidth: 96,
                    borderRadius: 10,
                    border: '1px dashed var(--aviary-disabled)',
                    background: 'var(--aviary-surface)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconLink size={22} style={{ color: 'var(--aviary-muted)' }} />
                </Box>
                <Stack gap={6} style={{ minWidth: 0 }}>
                  <Text fw={700} style={{ color: 'var(--aviary-ink)' }} lineClamp={2}>
                    {resolvedTitle || 'Untitled page'}
                  </Text>
                  <Group gap={8}>
                    <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
                      {resolvedDomain || 'Unknown domain'}
                    </Text>
                    <Text
                      size="sm"
                      style={{ color: resolved.reachable ? 'var(--mantine-color-sage-7)' : 'var(--mantine-color-red-7)' }}
                    >
                      {resolved.reachable ? '✓ reachable' : '× unreachable'}
                    </Text>
                  </Group>
                  <Text size="sm" style={{ color: 'var(--aviary-muted)' }} lineClamp={3}>
                    {resolved.error || previewDescription}
                  </Text>
                </Stack>
              </Group>
            </Paper>
          </Stack>
        )}

        <Paper
          p="sm"
          radius="md"
          style={{
            border: '1px dashed var(--mantine-color-sage-4)',
            background: 'var(--mantine-color-sage-0)',
          }}
        >
          <Text size="sm" style={{ color: 'var(--mantine-color-sage-8)' }}>
            ↩ Each recipient gets a unique magic link that redirects here. Opens are tracked per person and feed their temperature.
          </Text>
          <Text size="xs" c="dimmed" mt="xs">
            Because the page lives on {bannerDomain}, we can&apos;t measure dwell time, scroll depth, or reactions. For deeper engagement signals, upload the HTML instead.
          </Text>
        </Paper>

        <Group justify="space-between">
          <Button
            variant="default"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate(back.path)}
          >
            Back
          </Button>
          <Button
            color="sage"
            onClick={handleUseAndPromote}
            disabled={!canUse}
            loading={saving}
          >
            Use link &amp; promote »
          </Button>
        </Group>
      </Stack>
    </Box>
  );
}
